import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Popover, Select, message } from "antd";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import moment from "moment";
import "moment/locale/pt-br";
moment.locale("pt-br");
import {
  companiesService,
  dashboardBankHoursService,
  dashboardExtraHoursService,
  departmentsService
} from "./../../../redux/services";
import { palette } from "../../../config/theme";
import UIEmptyState from "../../EmptyState";
import "./index.css";

const MAX_DAYS = 31; // limite inclusivo do intervalo

// cores fixas por faixa de hora extra (ordem estável, repete se houver mais faixas)
const FAIXA_COLORS = [
  palette.violet500,
  palette.blue500,
  palette.cyan500,
  palette.green500,
  palette.amber500,
  palette.red500,
  palette.pink500,
  palette.neutral500,
];

const corFaixa = (i) => FAIXA_COLORS[i % FAIXA_COLORS.length];

// D = diurno, N = noturno, I = intervalo (sufixo do nome da faixa, ex: "Extra 50%D")
const FAIXA_ICONS = { D: "fad fa-sun", N: "far fa-moon", I: "far fa-mug-hot" };

const iconeFaixa = (nome) => FAIXA_ICONS[String(nome || "").trim().slice(-1).toUpperCase()] ?? null;

const isAll = (v) => v === "" || v === "all" || v == null;

const toMinutes = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? Math.round(v * 60) : 0;

  const negativo = String(v).trim().startsWith("-");
  const [h, m] = String(v).replace("-", "").split(":");
  const minutos = (Number(h) || 0) * 60 + (Number(m) || 0);

  return negativo ? -minutos : minutos;
};

const fromMinutes = (min) => {
  const sinal = min < 0 ? "-" : "";
  const abs = Math.abs(Math.round(min));
  return `${sinal}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};

// normaliza para: sem futuro, máximo 31 dias (inclusivo)
export function normalizeRange(vals) {
  const TODAY_END = moment().endOf("day");
  let [start, end] = vals || [];

  end = moment.isMoment(end) ? end.clone().endOf("day") : TODAY_END.clone();
  if (end.isAfter(TODAY_END)) end = TODAY_END.clone();

  start = moment.isMoment(start)
    ? start.clone().startOf("day")
    : end.clone().subtract(MAX_DAYS - 1, "days").startOf("day");

  if (end.diff(start, "days") + 1 > MAX_DAYS) {
    end = start.clone().add(MAX_DAYS - 1, "days").endOf("day");
  }

  return [start, end];
}

function clampRange(vals) {
  const [s, e] = normalizeRange(vals);
  if (e.diff(s, "days") + 1 > MAX_DAYS) {
    message.info(`Intervalo máximo é de ${MAX_DAYS} dias.`);
  }
  return [s, e];
}

const percentFormat = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatPercent = (value) => `${value > 0 ? "+" : ""}${percentFormat.format(value)}%`;

const formatSignedHours = (minutes) => `${minutes > 0 ? "+" : ""}${fromMinutes(minutes)}h`;

// variação percentual; null quando não há base de comparação (período anterior zerado)
const percentChange = (current, previous) => {
  if (!previous) return current ? null : 0;
  return ((current - previous) / Math.abs(previous)) * 100;
};

const STABLE_THRESHOLD = 1;

const trendOf = (change) =>
  change == null ? "new" : Math.abs(change) < STABLE_THRESHOLD ? "stable" : change > 0 ? "up" : "down";

function movementInsight(type, current, previous) {
  const change = percentChange(current, previous);
  const trend = current === 0 && previous === 0 ? "empty" : trendOf(change);
  const percentText = change == null ? "" : percentFormat.format(Math.abs(change));

  if (type === "debit") {
    return {
      empty: { tone: "neutral", icon: "far fa-minus", title: "Sem débitos", text: "Nenhum débito foi registrado no período nem no período anterior." },
      new: { tone: "bad", icon: "far fa-arrow-up", title: "Novos débitos", text: `Não houve débitos no período anterior. Neste período foram registradas ${fromMinutes(current)}h de débito.` },
      stable: { tone: "neutral", icon: "far fa-equals", title: "Débitos estáveis", text: "Os débitos se mantiveram estáveis em relação ao período anterior." },
      up: { tone: "bad", icon: "far fa-arrow-up", title: "Débitos em alta", text: `Os débitos aumentaram ${percentText}% em relação ao período anterior, indicando maior utilização do banco de horas.` },
      down: { tone: "good", icon: "far fa-arrow-down", title: "Débitos em queda", text: `Os débitos reduziram ${percentText}% em relação ao período anterior, aliviando o saldo do banco de horas.` },
    }[trend];
  }

  return {
    empty: { tone: "neutral", icon: "far fa-minus", title: "Sem créditos", text: "Nenhum crédito foi registrado no período nem no período anterior." },
    new: { tone: "good", icon: "far fa-arrow-up", title: "Novos créditos", text: `Não houve créditos no período anterior. Neste período foram registradas ${fromMinutes(current)}h de crédito.` },
    stable: { tone: "neutral", icon: "far fa-equals", title: "Créditos estáveis", text: "Os créditos se mantiveram estáveis em relação ao período anterior." },
    up: { tone: "good", icon: "far fa-arrow-up", title: "Créditos em alta", text: `Os créditos aumentaram ${percentText}% em relação ao período anterior, contribuindo positivamente para o saldo.` },
    down: { tone: "bad", icon: "far fa-arrow-down", title: "Créditos em queda", text: `Os créditos reduziram ${percentText}% em relação ao período anterior, impactando diretamente o saldo do período.` },
  }[trend];
}

function balanceInsight(balance) {
  if (balance < 0) {
    return { tone: "bad", icon: "far fa-exclamation", title: "Saldo negativo", text: `O período encerra com ${fromMinutes(balance)}h, exigindo atenção para evitar maior impacto no saldo geral.` };
  }
  if (balance > 0) {
    return { tone: "good", icon: "far fa-check", title: "Saldo positivo", text: `O período encerra com +${fromMinutes(balance)}h, que deverão ser compensadas ou pagas aos colaboradores.` };
  }
  return { tone: "neutral", icon: "far fa-equals", title: "Saldo zerado", text: "Créditos e débitos se equilibraram no período." };
}

const BankStat = ({ type, label, icon, value, previous, higherIsBetter, days }) => {
  const change = percentChange(value, previous);
  const trend = trendOf(change);
  const tone = trend === "stable" || trend === "new" ? "neutral" : (trend === "up") === higherIsBetter ? "good" : "bad";
  const trendIcon = { up: "far fa-arrow-up", down: "far fa-arrow-down", stable: "far fa-equals", new: "far fa-minus" }[trend];

  return (
    <div className={`byp-bank__stat byp-bank__stat--${type}`}>
      <div className="byp-bank__stat-head">
        <span className="byp-bank__stat-icon"><i className={icon} /></span>
        <span className="byp-bank__stat-label">{label}</span>
      </div>
      <strong
        className="byp-bank__stat-value"
        style={type === "balance" && value !== 0 ? { color: value < 0 ? palette.red600 : palette.green600 } : undefined}
      >
        {fromMinutes(value)}h
      </strong>
      <div className={`byp-bank__trend byp-bank__trend--${tone}`}>
        <span className="byp-bank__trend-icon"><i className={trendIcon} /></span>
        <div>
          <strong>{change == null ? "Sem base" : formatPercent(change)}</strong>
          <span>{change == null ? "sem dados no período anterior" : `vs. ${days} dias anteriores`}</span>
        </div>
      </div>
    </div>
  );
};

const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const isSerie = payload.length > 1 || payload[0]?.dataKey !== "total";

  if (!isSerie) {
    return (
      <div className="byp-hours__tooltip">
        <div className="byp-hours__tooltip-title">{label}</div>
        <strong>{fromMinutes(payload[0].value)} h</strong>
      </div>
    );
  }

  const linha = payload[0]?.payload;
  const itens = linha?.ordenado || [];
  const total = linha?.total ?? 0;

  return (
    <div className="byp-hours__tooltip byp-hours__tooltip--serie">
      <div className="byp-hours__tooltip-title">{label}</div>
      {itens
        .filter((item) => item.valor > 0)
        .reverse()
        .map((item) => (
          <div key={item.key} className="byp-hours__tooltip-row">
            <span className="byp-dot" style={{ background: item.cor }} />
            <span className="byp-hours__tooltip-row-name">{item.name}</span>
            <strong>{fromMinutes(item.valor)}h</strong>
          </div>
        ))}
      <div className="byp-hours__tooltip-row byp-hours__tooltip-row--total">
        <span className="byp-hours__tooltip-row-name">Total</span>
        <strong>{fromMinutes(total)}h</strong>
      </div>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
export default function HoursBank({
  endPoint = "banco_horas",
  title = "Banco de horas",
  empresa = "all",
  departamento = "",
  inicio = null,
  fim = null,
}) {
  const isSerie = endPoint !== "banco_horas";

  const defaultRange = useMemo(
    () => normalizeRange([inicio, fim]),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [emp, setEmp] = useState(empresa);
  const [dept, setDept] = useState(departamento);
  const [range, setRange] = useState(defaultRange);

  const [modalFilter, setModalFilter] = useState(false);
  const [empDraft, setEmpDraft] = useState(empresa);
  const [deptDraft, setDeptDraft] = useState(departamento);
  const [rangeDraft, setRangeDraft] = useState(defaultRange);

  // limites dinâmicos durante a seleção no calendário
  const [limit, setLimit] = useState({ min: null, max: null });

  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [empresas, setEmpresas] = useState([{ label: "Todas as Empresas", value: "all" }]);
  const [departamentos, setDepartamentos] = useState([{ label: "Todos os Departamentos", value: "" }]);

  const filterRef = useRef(null);

  // --- LOADERS ---
  async function carregaDados(nextEmp = emp, nextDept = dept, nextRange = range) {
    setLoadingChart(true);
    try {
      const options = {};
      if (!isAll(nextEmp)) options.empresa_uuid = nextEmp;
      if (!isAll(nextDept)) options.departamento_uuid = nextDept;
      if (Array.isArray(nextRange) && nextRange[0] && nextRange[1]) {
        options.inicio = nextRange[0].format("YYYY-MM-DD");
        options.fim = nextRange[1].format("YYYY-MM-DD");
      }

      if (isSerie) options.group_by = "dia";

      const resp = isSerie
        ? await dashboardExtraHoursService.getAll(options)
        : await dashboardBankHoursService.getAll(options);

      const dados = resp?.data?.data ?? resp?.data ?? resp ?? [];
      const cols = resp?.data?.columns ?? resp?.columns ?? [];

      setRows(Array.isArray(dados) ? dados : []);
      setColumns(Array.isArray(cols) ? cols : []);
      setSummary(resp?.data?.resumo ?? null);
    } catch (e) {
      console.error("Erro ao carregar dados do gráfico", e);
      setRows([]);
      setColumns([]);
      setSummary(null);
    } finally {
      setLoadingChart(false);
    }
  }

  async function carregaEmpresas() {
    setLoadingEmpresas(true);
    try {
      const response = await companiesService.getAll();
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setEmpresas((prev) => [prev[0], ...lista]);
    } catch (e) {
      console.error("Erro ao carregar empresas", e);
      setEmpresas([{ label: "Todas as empresas", value: "all" }]);
    } finally {
      setLoadingEmpresas(false);
    }
  }

  async function carregarDepartamentos(empUuid) {
    setLoadingDepartamentos(true);
    try {
      const options = {};
      if (!isAll(empUuid)) options.empresa_uuid = empUuid;
      const resp = await departmentsService.getAll(options);
      const brutos = resp?.data?.data ?? resp?.data ?? resp ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({ label: d.nome, value: d.uuid }));
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }, ...lista]);
    } catch (e) {
      console.error("Erro ao carregar departamentos", e);
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
    } finally {
      setLoadingDepartamentos(false);
    }
  }

  // --- INIT ---
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await carregaEmpresas();
      await carregaDados(emp, dept, range);
    })();
  }, []);

  const skipSyncRef = useRef(true);

  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const nextRange = normalizeRange([inicio, fim]);
    setEmp(empresa);
    setDept(departamento);
    setRange(nextRange);
    carregaDados(empresa, departamento, nextRange);
  }, [empresa, departamento, inicio, fim]);

  useEffect(() => {
    if (!modalFilter) return;
    if (isAll(empDraft)) {
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
      return;
    }
    carregarDepartamentos(empDraft);
  }, [empDraft, modalFilter]);

  const openFilter = () => {
    setEmpDraft(emp || "all");
    setDeptDraft(dept || "");
    setRangeDraft(range);
    setModalFilter(true);
  };

  const handleApply = async () => {
    const nextEmp = empDraft || "all";
    const nextDept = deptDraft || "";
    const nextRange = normalizeRange(rangeDraft);
    setEmp(nextEmp);
    setDept(nextDept);
    setRange(nextRange);
    setModalFilter(false);
    await carregaDados(nextEmp, nextDept, nextRange);
  };

  // --- Banco de horas: totais do período ---
  const totais = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        credito: acc.credito + toMinutes(r.credito),
        debito: acc.debito + toMinutes(r.debito),
        saldo: acc.saldo + toMinutes(r.saldo),
      }),
      { credito: 0, debito: 0, saldo: 0 }
    );
  }, [rows]);

  // --- Horas extras: série diária + totais por faixa ---
  const chaves = useMemo(
    () => (columns.length ? columns.map((c) => c.key) : ["credito"]),
    [columns]
  );

  const serie = useMemo(() => {
    if (!isSerie) return [];

    return rows.map((r, i) => {
      const dia = moment(r.name, "YYYY-MM-DD", true);
      let total = 0;

      const itens = chaves.map((key, idx) => {
        const min = toMinutes(r[key]);
        total += min;
        const col = columns[idx] || { key, name: key };
        return { key, name: col.name, valor: min, cor: corFaixa(idx) };
      });

      // maior valor na base da pilha (posição 0), menor valor no topo
      const ordenado = itens.slice().sort((a, b) => b.valor - a.valor);
      const posValores = {};
      ordenado.forEach((item, posIdx) => {
        posValores[`pos${posIdx}`] = item.valor;
      });

      return {
        label: dia.isValid() ? dia.format("DD/MM") : String(r.name ?? `#${i + 1}`),
        ordem: dia.isValid() ? dia.valueOf() : i,
        total,
        ordenado,
        ...posValores,
      };
    }).sort((a, b) => a.ordem - b.ordem);
  }, [rows, chaves, columns, isSerie]);

  const totaisFaixa = useMemo(() => {
    if (!isSerie) return [];
    return columns.map((c) => ({
      key: c.key,
      name: c.name,
      minutos: rows.reduce((sum, r) => sum + toMinutes(r[c.key]), 0),
    }));
  }, [rows, columns, isSerie]);

  const currentPeriod = summary?.periodo ?? totais;
  const previousPeriod = summary?.anterior ?? { credito: 0, debito: 0, saldo: 0 };
  const overallBalance = summary?.saldo_geral ?? currentPeriod.saldo;
  const periodDays = summary?.dias ?? (range?.[1] && range?.[0] ? range[1].diff(range[0], "days") + 1 : MAX_DAYS);

  const insights = useMemo(
    () => [
      { key: "debit", ...movementInsight("debit", currentPeriod.debito, previousPeriod.debito) },
      { key: "credit", ...movementInsight("credit", currentPeriod.credito, previousPeriod.credito) },
      { key: "balance", ...balanceInsight(currentPeriod.saldo) },
    ],
    [currentPeriod.debito, currentPeriod.credito, currentPeriod.saldo, previousPeriod.debito, previousPeriod.credito]
  );

  const balanceSign = (minutes) => (minutes < 0 ? "negative" : minutes > 0 ? "positive" : "zero");

  const periodAlert = {
    negative: { tone: "bad", icon: "fas fa-exclamation-triangle", text: "O saldo do período está negativo. Considere ações para reequilibrar o banco de horas." },
    positive: { tone: "good", icon: "fas fa-check-circle", text: "O saldo do período está positivo. Acompanhe a compensação das horas acumuladas pelos colaboradores." },
    zero: { tone: "neutral", icon: "fas fa-info-circle", text: "Créditos e débitos estão equilibrados no período." },
  }[balanceSign(currentPeriod.saldo)];

  const overallAlert = {
    negative: "O saldo geral do banco de horas está negativo. Avalie o planejamento de horas para os próximos períodos.",
    positive: "O saldo geral do banco de horas está positivo. Planeje a compensação das horas acumuladas para os próximos períodos.",
    zero: "O saldo geral do banco de horas está zerado.",
  }[balanceSign(overallBalance)];

  const totalSerie = serie.reduce((sum, d) => sum + d.total, 0);
  const mediaDiaria = serie.length ? totalSerie / serie.length : 0;

  // domínio/ticks do eixo Y calculados manualmente: o "nice ticks" automático do Recharts
  // não cobre corretamente o total empilhado das barras quando os valores (em minutos) são
  // grandes, gerando rótulos de eixo desalinhados com a altura real das barras
  const eixoY = useMemo(() => {
    const TICKS = 5;
    const PASSOS_HORA = [1, 2, 3, 5, 10, 15, 20, 25, 30, 50, 100, 150, 200];
    const maiorTotal = serie.reduce((max, d) => Math.max(max, d.total), 0);
    const maiorHoras = maiorTotal / 60;

    const passo = PASSOS_HORA.find((p) => maiorHoras / TICKS <= p) || Math.ceil(maiorHoras / TICKS);
    const maxHoras = Math.max(passo, Math.ceil(maiorHoras / passo) * passo);

    return {
      domain: [0, maxHoras * 60],
      ticks: Array.from({ length: TICKS + 1 }, (_, i) => i * passo * 60),
    };
  }, [serie]);

  // --- Calendário: sem futuro e janela máxima de 31 dias ---
  const disabledDate = (current) => {
    if (!current) return false;
    if (current.isAfter(moment().endOf("day"), "day")) return true;
    if (limit.min && current.isBefore(limit.min, "day")) return true;
    if (limit.max && current.isAfter(limit.max, "day")) return true;
    return false;
  };

  const filtroContent = (
    <div className="byp-card__filter" ref={filterRef}>
      <label>
        Empresa
        <Select
          value={empDraft}
          onChange={(v) => {
            setEmpDraft(v);
            setDeptDraft("");
          }}
          options={empresas}
          size="middle"
          style={{ width: "100%" }}
          loading={loadingEmpresas}
          getPopupContainer={() => filterRef.current || document.body}
        />
      </label>
      <label>
        Departamento
        <Select
          value={deptDraft}
          onChange={setDeptDraft}
          options={departamentos}
          size="middle"
          style={{ width: "100%" }}
          loading={loadingDepartamentos}
          disabled={isAll(empDraft)}
          notFoundContent="Nenhum departamento encontrado"
          getPopupContainer={() => filterRef.current || document.body}
        />
      </label>
      <label>
        Período
        <DatePicker.RangePicker
          value={rangeDraft}
          onCalendarChange={(dates) => {
            // ao escolher a 1ª data, prende a outra ponta à janela de 31 dias
            if (dates && dates[0] && !dates[1]) {
              const first = dates[0].clone().startOf("day");
              setLimit({
                min: first.clone().subtract(MAX_DAYS - 1, "days").startOf("day"),
                max: first.clone().add(MAX_DAYS - 1, "days").endOf("day"),
              });
            } else {
              setLimit({ min: null, max: null });
            }
          }}
          onChange={(vals) => setRangeDraft(clampRange(vals))}
          disabledDate={disabledDate}
          style={{ width: "100%" }}
          allowClear={false}
          format="DD/MM/YYYY"
          ranges={{
            "Últimos 7 dias": [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
            "Últimos 15 dias": [moment().subtract(14, "days").startOf("day"), moment().endOf("day")],
            "Últimos 31 dias": [moment().subtract(30, "days").startOf("day"), moment().endOf("day")],
          }}
          getPopupContainer={() => filterRef.current || document.body}
        />
      </label>
      <div className="byp-card__filter-actions">
        <Button size="small" onClick={() => setModalFilter(false)}>Cancelar</Button>
        <Button size="small" type="primary" onClick={handleApply}>Filtrar</Button>
      </div>
    </div>
  );

  return (
    <section className={`byp-card byp-hours ${isSerie ? "byp-hours--serie" : "byp-hours--resumo"}`}>
      <header className="byp-card__head">
        <div>
          <h3 className="byp-card__title">
            <span className="byp-card__title-icon">
              <i className={isSerie ? "fad fa-business-time" : "fad fa-hourglass-half"} />
            </span>
            {title}
          </h3>
          <span className="byp-card__subtitle">
            {!isSerie && "Período: "}
            {range?.[0]?.format("DD/MM/YYYY")} - {range?.[1]?.format("DD/MM/YYYY")}
          </span>
        </div>
        <div className="byp-card__head-actions">
          <Popover
            content={filtroContent}
            trigger="click"
            placement="bottomRight"
            visible={modalFilter}
            onVisibleChange={(v) => (v ? openFilter() : setModalFilter(false))}
            overlayClassName="byp-card__filter-overlay"
          >
            <button type="button" className="byp-card__action byp-card__action--icon" aria-label="Filtrar">
              <i className="far fa-filter" />
            </button>
          </Popover>
        </div>
      </header>

      {isSerie ? (
        <div className="byp-hours__body">
          <div className="byp-hours__legend-row">
            {columns.map((c, i) => (
              <span key={c.key} className="byp-hours__legend-item">
                <span className="byp-hours__legend-swatch" style={{ background: corFaixa(i) }} />
                {c.name}
              </span>
            ))}
          </div>

          <div className="byp-hours__chart">
            {serie.length === 0 ? (
              <p className="byp-card__empty">Nenhum registro no período</p>
            ) : (
              <div className="byp-hours__plot">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serie} margin={{ top: 24, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--byp-neutral-100)" />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: palette.neutral400, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                      minTickGap={12}
                    />
                    <YAxis
                      tick={{ fill: palette.neutral400, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                      domain={eixoY.domain}
                      ticks={eixoY.ticks}
                      tickFormatter={(v) => `${Math.round(v / 60)}h`}
                    />
                    <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(139, 92, 246, .08)" }} />
                    {chaves.map((_, posIdx) => (
                      <Bar
                        key={`pos${posIdx}`}
                        dataKey={`pos${posIdx}`}
                        stackId="faixas"
                        maxBarSize={18}
                        animationDuration={500}
                      >
                        {serie.map((d, i) => {
                          const topIndex = d.ordenado.reduce((lastIndex, item, index) => (item.valor > 0 ? index : lastIndex), 0);
                          const isTop = posIdx === topIndex;
                          const isBase = posIdx === 0;
                          const radius = [isTop ? 4 : 0, isTop ? 4 : 0, isBase ? 4 : 0, isBase ? 4 : 0];
                          return <Cell key={i} fill={d.ordenado[posIdx]?.cor || palette.neutral300} radius={radius} />;
                        })}
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="byp-hours__kpis">
            <div className="byp-hours__kpi-summary">
              <div className="byp-hours__kpi byp-hours__kpi--total">
                <span className="byp-hours__kpi-icon" style={{ background: palette.violet500 }}>
                  <i className="far fa-layer-group" />
                </span>
                <div className="byp-hours__kpi-text">
                  <span className="byp-metric__label">Total</span>
                  <strong className="byp-metric__value">{fromMinutes(totalSerie)}h</strong>
                </div>
              </div>
              <div className="byp-hours__kpi">
                <span className="byp-hours__kpi-icon" style={{ background: palette.neutral500 }}>
                  <i className="far fa-calendar-day" />
                </span>
                <div className="byp-hours__kpi-text">
                  <span className="byp-metric__label">Média diária</span>
                  <strong className="byp-metric__value">{fromMinutes(mediaDiaria)} h</strong>
                </div>
              </div>
            </div>
            <div className="byp-hours__kpi-faixas">
              {totaisFaixa.map((f, i) => {
                const icone = iconeFaixa(f.name);
                return (
                  <div key={f.key} className="byp-hours__kpi">
                    <span className="byp-hours__kpi-icon" style={{ background: corFaixa(i) }}>
                      <i className={icone || "far fa-clock"} />
                    </span>
                    <div className="byp-hours__kpi-text">
                      <span className="byp-metric__label">{f.name}</span>
                      <strong className="byp-metric__value">{fromMinutes(f.minutos)}h</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : currentPeriod.credito === 0 && currentPeriod.debito === 0 ? (
        <UIEmptyState
          icon={<i className="fal fa-hourglass-half" />}
          title="Não há dados"
          description="Nenhuma movimentação de banco de horas no período selecionado."
        />
      ) : (
        <div className="byp-bank">
          <div className="byp-bank__stats">
            <BankStat
              type="debit"
              label="Débito"
              icon="far fa-arrow-down"
              value={currentPeriod.debito}
              previous={previousPeriod.debito}
              higherIsBetter={false}
              days={periodDays}
            />
            <BankStat
              type="credit"
              label="Crédito"
              icon="far fa-arrow-up"
              value={currentPeriod.credito}
              previous={previousPeriod.credito}
              higherIsBetter
              days={periodDays}
            />
            <BankStat
              type="balance"
              label="Saldo"
              icon="far fa-equals"
              value={currentPeriod.saldo}
              previous={previousPeriod.saldo}
              higherIsBetter
              days={periodDays}
            />
          </div>

          <div className="byp-bank__insights">
            <div className="byp-bank__insights-head">
              <span className="byp-bank__insights-icon"><i className="fad fa-lightbulb-on" /></span>
              <h4>Principais Insights</h4>
            </div>
            <div className="byp-bank__insights-list">
              {insights.map((item) => (
                <div key={item.key} className={`byp-bank__insight byp-bank__insight--${item.tone}`}>
                  <div className="byp-bank__insight-title">
                    <span className="byp-bank__insight-icon"><i className={item.icon} /></span>
                    <strong>{item.title}</strong>
                  </div>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="byp-bank__balances">
            <div className={`byp-bank__balance byp-bank__balance--period byp-bank__balance--${periodAlert.tone}`}>
              <div className="byp-bank__balance-head">
                <span className="byp-bank__balance-icon"><i className="fad fa-calendar-alt" /></span>
                <div>
                  <strong>Saldo do Período</strong>
                  <span>Créditos - Débitos</span>
                </div>
              </div>
              <strong
                className="byp-bank__balance-value"
                style={currentPeriod.saldo !== 0 ? { color: currentPeriod.saldo < 0 ? palette.red600 : palette.green600 } : undefined}
              >
                {formatSignedHours(currentPeriod.saldo)}
              </strong>
              <div className="byp-bank__alert">
                <i className={periodAlert.icon} />
                <span>{periodAlert.text}</span>
              </div>
            </div>

            <div className="byp-bank__balance byp-bank__balance--overall">
              <div className="byp-bank__balance-head">
                <span className="byp-bank__balance-icon"><i className="fad fa-university" /></span>
                <div>
                  <strong>Saldo Geral do Banco de Horas</strong>
                  <span>Acumulado de todos os períodos</span>
                </div>
              </div>
              <strong className="byp-bank__balance-value">{formatSignedHours(overallBalance)}</strong>
              <div className="byp-bank__alert">
                <i className="fas fa-info-circle" />
                <span>{overallAlert}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingChart && <div className="byp-card__loading">Carregando…</div>}
    </section>
  );
}
